import clsx from 'clsx';
import { useRef, useState } from 'react';
import Carousel from 'react-multi-carousel';
import { Direction } from 'types/Utilities';
import styles from 'components/ui/ContentBlocks/BeforeAfterPhotos.module.scss';
import CustomSliderArrow from 'components/ui/CustomSliderArrow';
import Icon from 'components/ui/Icon';
import { default as ImageComponent } from 'components/ui/Image';
import tailwind from 'utils/breakpoints';
import { replaceSpecialChar } from 'utils/string';
import { useTranslation } from 'hooks/useTranslation';
import { useVerticalSwipePreventer } from 'hooks/useVerticalSwipePreventer';
import type { Image } from 'types/Utilities';

type Props = {
  photos: Image[];
};

const ClinicPhotos = ({ photos: _photos }: Props): JSX.Element => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [curSlide, setCurSlide] = useState(0);
  const [photoIndex, setPhotoIndex] = useState(0);
  const [photos, setPhotos] = useState(_photos.slice(0, 4));
  const { t } = useTranslation();
  useVerticalSwipePreventer(containerRef);

  return (
    <div
      id="photos"
      className="border-t border-silver-light lg:border-0 md:pt-18 lg:hidden"
    >
      <div className="max-w-screen-xl mx-auto px-5 md:px-4">
        <div className="md:grid grid-cols-3">
          <div className="flex justify-between items-center md:items-start">
            <h2 className="adelle-bold-section-headline">
              {t('clinicPage.clinicPhotos')}
            </h2>
            <Icon
              type="Arrow"
              direction={isCollapsed ? Direction.BOTTOM : Direction.TOP}
              onClick={() => {
                setIsCollapsed(!isCollapsed);
              }}
              className="md:hidden h-4 w-4 mb-6 text-navy stroke-current mt-12"
              color={tailwind.theme.backgroundColor.navy}
            />
          </div>
          <div
            className={clsx(
              isCollapsed && 'hidden',
              'lg:block col-span-2 text-charcoal',
            )}
          >
            <div
              className="-mx-6 lg:mx-0 mb-12 relative w-full"
              ref={containerRef}
            >
              <Carousel
                responsive={{
                  other: {
                    breakpoint: { min: 768, max: 4000 },
                    items: 1,
                    partialVisibilityGutter: 255,
                  },
                  mobile: {
                    breakpoint: { min: 0, max: 768 },
                    items: 1,
                    partialVisibilityGutter: 40,
                  },
                }}
                arrows
                partialVisible
                removeArrowOnDeviceType={['mobile']}
                customLeftArrow={
                  <CustomSliderArrow
                    direction="left"
                    className={styles.carouselArrowLeft}
                  />
                }
                customRightArrow={
                  <CustomSliderArrow
                    direction="right"
                    className={styles.carouselArrowRight}
                  />
                }
                containerClass={clsx(
                  'flex relative items-center lg:overflow-hidden px-6 md:pl-8',
                  styles.treatmentsCarousel,
                  styles.showBefore,
                  curSlide !== photos.length - 1 && styles.showAfter,
                )}
                sliderClass="flex relative flex-row list-none p-0 m-0"
                itemClass={styles.carouselItem}
                beforeChange={(nextSlide) => {
                  if (nextSlide > curSlide) {
                    const nextPhotos = photos.slice(
                      photoIndex + 4,
                      photoIndex + 8,
                    );
                    setPhotos(photos.concat(nextPhotos));
                    setPhotoIndex(photoIndex + 4);
                  }

                  setCurSlide(nextSlide);
                }}
              >
                {photos.map((photo, i) => {
                  const { description, title, url } = photo || {};

                  return (
                    <div
                      key={i}
                      className="slide border border-silver-light overflow-hidden rounded-lg shrink-0 mr-2"
                    >
                      <div className="flex">
                        <div className="w-full h-40 lg:h-49 relative">
                          <ImageComponent
                            className="self-center w-full h-full object-cover"
                            src={url}
                            alt={
                              description || title
                                ? replaceSpecialChar(description || title)
                                : ''
                            }
                            fill
                            contentful
                          />
                        </div>
                      </div>
                      <div
                        className={clsx(
                          'bg-white text-base py-2 pb-3 px-4 border-t relative',
                          styles.label,
                        )}
                      >
                        {description && replaceSpecialChar(description)}
                      </div>
                    </div>
                  );
                })}
              </Carousel>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ClinicPhotos;
